import type { CaptureAttributionInput } from '__generated__/graphql'
import { ANALYTICS_LIMITS, sanitizeUrl } from './sanitize'

/**
 * Marketing attribution captured from the landing URL (FE-320).
 *
 * The browser is the only place where UTM tags and ad click ids are visible,
 * so the frontend reads them once per page load, buffers them for the tab and
 * reports them with `captureAttribution` for the current guest session or
 * user. First / last touch and the carry-over to the user on merge are the
 * backend's job; `fbc` / `fbp` are not sent — no Meta Pixel sets them here.
 */

export const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content'
] as const

export const CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'ttclid'
] as const

/** Privy appends these when an OAuth login comes back to the app. */
const OAUTH_RETURN_PARAM_PREFIX = 'privy_oauth_'
/** Pages an OAuth login comes back from (Google, Apple, Privy). */
const OAUTH_REFERRER_HOSTS: readonly string[] = [
  'accounts.google.com',
  'appleid.apple.com',
  'auth.privy.io'
]

type UtmParam = (typeof UTM_PARAMS)[number]
type ClickIdParam = (typeof CLICK_ID_PARAMS)[number]

export interface Attribution {
  utm?: Partial<Record<UtmParam, string>>
  clickIds?: Partial<Record<ClickIdParam, string>>
  /** External referrer; same-origin navigations are not a referrer. */
  referrer?: string
  landingUrl: string
  /** ISO timestamp of the capture, so the server can order touches. */
  capturedAt: string
}

export interface AttributionSource {
  /** `window.location.search` */
  search: string
  /** `window.location.href` */
  href: string
  /** `window.location.origin` */
  origin: string
  /** `document.referrer` */
  referrer: string
  now?: Date
}

/** Trimmed text within `maxLength`, or `undefined` when empty. */
const cleanText = (
  value: unknown,
  maxLength: number = ANALYTICS_LIMITS.textLength
) => {
  const trimmed = typeof value === 'string' ? value.trim() : ''

  return trimmed ? trimmed.slice(0, maxLength) : undefined
}

const parseUrl = (value: string) => {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

const pickParams = <K extends string>(
  params: URLSearchParams,
  keys: readonly K[]
): Partial<Record<K, string>> | undefined => {
  const picked: Partial<Record<K, string>> = {}

  for (const key of keys) {
    const value = cleanText(params.get(key))
    if (value) picked[key] = value
  }

  return Object.keys(picked).length ? picked : undefined
}

const pickExternalReferrer = (referrer: string, origin: string) => {
  const url = parseUrl(referrer.trim())

  return url && url.origin !== origin ?
      cleanText(referrer, ANALYTICS_LIMITS.urlLength)
    : undefined
}

const isOAuthReturn = (params: URLSearchParams, referrer: string) =>
  [...params.keys()].some(key => key.startsWith(OAUTH_RETURN_PARAM_PREFIX)) ||
  OAUTH_REFERRER_HOSTS.includes(parseUrl(referrer)?.hostname ?? '')

/**
 * Returns the attribution of this page load, or `null` when the URL carries
 * no marketing tags and the visitor came directly or from the app itself —
 * the backend is never sent an empty payload. The return from an OAuth login
 * is not a touch either: the tags of that visit were captured before the
 * redirect and still wait in the tab's buffer.
 */
export const captureAttribution = ({
  search,
  href,
  origin,
  referrer,
  now = new Date()
}: AttributionSource): Attribution | null => {
  const params = new URLSearchParams(search)
  if (isOAuthReturn(params, referrer)) return null

  const utm = pickParams(params, UTM_PARAMS)
  const clickIds = pickParams(params, CLICK_ID_PARAMS)
  const externalReferrer = pickExternalReferrer(referrer, origin)

  if (!utm && !clickIds && !externalReferrer) return null

  return {
    ...(utm && { utm }),
    ...(clickIds && { clickIds }),
    ...(externalReferrer && { referrer: externalReferrer }),
    landingUrl: href.slice(0, ANALYTICS_LIMITS.urlLength),
    capturedAt: now.toISOString()
  }
}

/**
 * Flattens a buffered attribution into the `captureAttribution` input. The
 * buffer is read back from sessionStorage, and one field over its limit
 * makes the backend refuse the whole call, so every value is trimmed again
 * and the URLs lose what must not leave the browser.
 */
export const toCaptureAttributionInput = ({
  utm = {},
  clickIds = {},
  referrer,
  landingUrl,
  capturedAt
}: Attribution): CaptureAttributionInput => {
  const cleanReferrer = cleanText(referrer, ANALYTICS_LIMITS.urlLength)

  return {
    utmSource: cleanText(utm.utm_source),
    utmMedium: cleanText(utm.utm_medium),
    utmCampaign: cleanText(utm.utm_campaign),
    utmTerm: cleanText(utm.utm_term),
    utmContent: cleanText(utm.utm_content),
    gclid: cleanText(clickIds.gclid),
    gbraid: cleanText(clickIds.gbraid),
    wbraid: cleanText(clickIds.wbraid),
    fbclid: cleanText(clickIds.fbclid),
    ttclid: cleanText(clickIds.ttclid),
    referrer: cleanReferrer && sanitizeUrl(cleanReferrer),
    landingUrl: sanitizeUrl(landingUrl),
    capturedAt
  }
}
